'use client';

import { FC, useState, useMemo } from 'react';
import Image from 'next/image';
import { Club, Player } from '@repo/types';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toMillionPrice } from '../../lib/utils';
import {
  X,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronFirst,
  ChevronLast,
  ArrowDown10,
  ArrowUp01,
  ArrowDownZA,
  ArrowUpAZ,
  type LucideIcon,
} from 'lucide-react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { PlayerActionModal } from './player-action-modal';

type SortOption =
  | 'price-desc'
  | 'price-asc'
  | 'name-asc'
  | 'name-desc'
  | 'points-desc'
  | 'selected-by-desc'
  | 'points-per-million-desc';

type SortValueType = 'number' | 'string';
type SortDirection = 'asc' | 'desc';

const sortOptionMeta: Record<
  SortOption,
  { label: string; valueType: SortValueType; direction: SortDirection }
> = {
  'price-desc': { label: 'Prix', valueType: 'number', direction: 'desc' },
  'price-asc': { label: 'Prix', valueType: 'number', direction: 'asc' },
  'name-asc': { label: 'Nom', valueType: 'string', direction: 'asc' },
  'name-desc': { label: 'Nom', valueType: 'string', direction: 'desc' },
  'points-desc': { label: 'Points', valueType: 'number', direction: 'desc' },
  'selected-by-desc': { label: 'Sélection %', valueType: 'number', direction: 'desc' },
  'points-per-million-desc': { label: 'Points par million', valueType: 'number', direction: 'desc' },
};

const getSortIcon = (valueType: SortValueType, direction: SortDirection): LucideIcon => {
  if (valueType === 'number') {
    return direction === 'asc' ? ArrowUp01 : ArrowDown10;
  }

  return direction === 'asc' ? ArrowUpAZ : ArrowDownZA;
};

const getNumericValue = (driver: Player) => {
  const rawValue = driver?.value;
  const numericValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue ?? 0;
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getDriverPoints = (driver: Player) => {
  return typeof driver.points === 'number' && Number.isFinite(driver.points) ? driver.points : 0;
};

type DriverInfo = {
  selectedBy?: number | string;
  specialty?: string;
};

const parseDriverInfo = (driver: Player): DriverInfo | null => {
  const info = driver.info;
  if (!info) return null;

  if (typeof info === 'string') {
    try {
      return JSON.parse(info) as DriverInfo;
    } catch {
      return null;
    }
  }

  if (typeof info === 'object' && info !== null) {
    return info as DriverInfo;
  }

  return null;
};

const getSelectedByPercentage = (driver: Player) => {
  const parsed = parseDriverInfo(driver);
  const selectedBy = parsed?.selectedBy;
  const numericSelectedBy = typeof selectedBy === 'string' ? parseFloat(selectedBy) : selectedBy;
  return typeof numericSelectedBy === 'number' && Number.isFinite(numericSelectedBy) ? numericSelectedBy : 0;
};

const getDriverSpecialty = (driver: Player): string | null => {
  const specialty = parseDriverInfo(driver)?.specialty;
  return typeof specialty === 'string' && specialty.trim() ? specialty : null;
};

const getPointsPerMillion = (driver: Player) => {
  const numericValue = getNumericValue(driver);
  if (numericValue <= 0) return 0;
  return getDriverPoints(driver) / numericValue;
};

interface PlayerListProps {
  clubs: Club[];
  drivers: Player[];
  selectionType: 'fantasyPick' | 'predictionPick';
  onSelect: (driver: Player) => void;
  onRemove: (driver: Player) => void;
  isEditable: boolean;
  selectionCheck: (driver: Player) => string;
  isTransferMode: boolean;
  containerId?: string;
}

/**
 * A table-based list of players with:
 * - search input (case-insensitive) for name/short
 * - filter by clubId
 * - filter by max value (2.00 - 12.00)
 * - pagination (10 players/page)
 * - striped rows
 * - "KIES" / "VERWIJDER" button logic
 */
export const PlayerList: FC<PlayerListProps> = ({
  drivers,
  onSelect,
  onRemove,
  isEditable,
  selectionCheck,
  isTransferMode,
  clubs, // fallback if not provided
  containerId,
}) => {
  const { competitionConfig } = useCompetitionContext();
  const transferTaxPercentage = competitionConfig.transferTaxPercentage || 0;
  const hasTransferTax = isTransferMode && transferTaxPercentage > 0;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClub, setSelectedClub] = useState<'all' | number>('all');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('all');
  const [maxValue, setMaxValue] = useState<number>(20);
  const [sortOption, setSortOption] = useState<SortOption>('price-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDriver, setSelectedDriver] = useState<Player | null>(null);

  const itemsPerPage = 10;

  const specialties = useMemo(() => {
    const set = new Set<string>();
    for (const driver of drivers) {
      const specialty = getDriverSpecialty(driver);
      if (driver.active !== 0 && specialty) {
        set.add(specialty);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [drivers]);

  const filteredDrivers = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();

    return drivers.filter((driver) => {
      if(driver.active === 0) return false; // Skip inactive drivers

      // 1) Search filter
      //    Checking in driver.name + driver.short (if exists) ignoring case
      const nameMatch =
        driver.name.toLowerCase().includes(lowerSearch) ||
        (driver.short?.toLowerCase().includes(lowerSearch) ?? false);

      if (!nameMatch) return false;

      // 2) Club filter
      if (selectedClub !== 'all' && driver.clubId !== selectedClub) {
        return false;
      }

      // 3) Specialty filter
      if (selectedSpecialty !== 'all' && getDriverSpecialty(driver) !== selectedSpecialty) {
        return false;
      }

      // 4) Value filter
      if (toMillionPrice(driver.value) > maxValue * 1000000) {
        return false;
      }

      return true;
    });
  }, [drivers, searchTerm, selectedClub, selectedSpecialty, maxValue]);

  const sortedDrivers = useMemo(() => {
    const sorted = [...filteredDrivers];

    sorted.sort((left, right) => {
      const leftPrice = getNumericValue(left);
      const rightPrice = getNumericValue(right);
      const leftName = left.name.toLowerCase();
      const rightName = right.name.toLowerCase();
      const leftPoints = getDriverPoints(left);
      const rightPoints = getDriverPoints(right);
      const leftSelectedBy = getSelectedByPercentage(left);
      const rightSelectedBy = getSelectedByPercentage(right);
      const leftPointsPerMillion = getPointsPerMillion(left);
      const rightPointsPerMillion = getPointsPerMillion(right);

      switch (sortOption) {
        case 'price-asc':
          return leftPrice - rightPrice || leftName.localeCompare(rightName);
        case 'name-asc':
          return leftName.localeCompare(rightName) || rightPrice - leftPrice;
        case 'name-desc':
          return rightName.localeCompare(leftName) || rightPrice - leftPrice;
        case 'points-desc':
          return rightPoints - leftPoints || rightPrice - leftPrice || leftName.localeCompare(rightName);
        case 'selected-by-desc':
          return rightSelectedBy - leftSelectedBy || rightPrice - leftPrice || leftName.localeCompare(rightName);
        case 'points-per-million-desc':
          return rightPointsPerMillion - leftPointsPerMillion || rightPoints - leftPoints || rightPrice - leftPrice || leftName.localeCompare(rightName);
        case 'price-desc':
        default:
          return rightPrice - leftPrice || leftName.localeCompare(rightName);
      }
    });

    return sorted;
  }, [filteredDrivers, sortOption]);

  // Pagination
  const totalItems = sortedDrivers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDrivers = sortedDrivers.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const selectedDriverStatus = selectedDriver ? selectionCheck(selectedDriver) : '';
  const selectedDriverClubName = selectedDriver
    ? clubs.find((club) => club.id === selectedDriver.clubId)?.name
    : undefined;
  const activeSortMeta = sortOptionMeta[sortOption];
  const ActiveSortIcon = getSortIcon(activeSortMeta.valueType, activeSortMeta.direction);
  const showStatColumn = sortOption === 'points-desc' || sortOption === 'selected-by-desc' || sortOption === 'points-per-million-desc';
  const hasActiveFilters =
    searchTerm !== '' ||
    selectedClub !== 'all' ||
    selectedSpecialty !== 'all' ||
    maxValue !== 20 ||
    sortOption !== 'price-desc';

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedClub('all');
    setSelectedSpecialty('all');
    setMaxValue(20);
    setSortOption('price-desc');
    setCurrentPage(1);
  };

  return (
    <div id={containerId} className="flex flex-col">
      {/* Filters */}
      <div className="space-y-3 border-b border-gray-200 bg-gray-50 p-4 sm:p-5">
        <div>
          <Label htmlFor="search" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
            Rechercher par nom
          </Label>
          <Input
            id="search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Rechercher un coureur"
            className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-400"
          />
        </div>

        <div className="flex gap-4">
          <div className="w-1/2">
            <Label htmlFor="clubSelect" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Équipe
            </Label>
            <Select
              value={selectedClub.toString()}
              onValueChange={(value) => {
                setSelectedClub(value === 'all' ? 'all' : parseInt(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger
                id="clubSelect"
                className="w-full border-gray-300 bg-white text-gray-900 shadow-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les équipes</SelectItem>
                {clubs.map((club) => (
                  <SelectItem key={club.id} value={club.id.toString()}>
                    {club.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-1/2">
            <Label htmlFor="valueSelect" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Valeur max
            </Label>
            <Select
              value={maxValue.toString()}
              onValueChange={(value) => {
                setMaxValue(parseFloat(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger
                id="valueSelect"
                className="w-full border-gray-300 bg-white text-gray-900 shadow-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...Array(19)].map((_, i) => 20 - i).map((value) => (
                  <SelectItem key={value} value={value.toString()}>
                    €{value}m
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-4">
          {specialties.length > 0 && (
            <div className="w-1/2">
              <Label htmlFor="specialtySelect" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Profil
              </Label>
              <Select
                value={selectedSpecialty}
                onValueChange={(value) => {
                  setSelectedSpecialty(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger
                  id="specialtySelect"
                  className="w-full border-gray-300 bg-white text-gray-900 shadow-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les profils</SelectItem>
                  {specialties.map((specialty) => (
                    <SelectItem key={specialty} value={specialty}>
                      {specialty}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className={specialties.length > 0 ? 'w-1/2' : 'w-full'}>
            <Label htmlFor="sortSelect" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
              Trier par
            </Label>
            <Select
              value={sortOption}
              onValueChange={(value) => {
                setSortOption(value as SortOption);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger
                id="sortSelect"
                className="w-full border-gray-300 bg-white text-gray-900 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <ActiveSortIcon className="h-4 w-4 text-gray-500" />
                  <span>{activeSortMeta.label}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(sortOptionMeta) as [SortOption, (typeof sortOptionMeta)[SortOption]][]).map(([value, meta]) => {
                  const SortIcon = getSortIcon(meta.valueType, meta.direction);
                  return (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <SortIcon className="h-4 w-4 text-gray-500" />
                        <span>{meta.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={handleResetFilters}
            disabled={!hasActiveFilters}
            className="h-8 rounded-full px-3 text-xs font-semibold disabled:cursor-not-allowed"
          >
            Réinitialiser les filtres
          </Button>
        </div>

      </div>

      {/* Table of players */}
      <div className="overflow-y-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-[#e6edf5] text-[#003d60]">
              <th className="w-12 py-2 pl-4 pr-1 text-left text-[10px] font-bold uppercase tracking-[0.08em]"></th>
              <th className="p-2 text-left text-[12px] font-bold uppercase tracking-[0.08em]">Nom</th>
              <th className="p-2 text-right text-[12px] font-bold uppercase tracking-[0.08em]">Prix</th>
              {showStatColumn && (
                <th className="p-2 text-right text-[10px] font-bold uppercase tracking-[0.08em]">
                  {sortOption === 'points-per-million-desc'
                    ? 'PPM'
                    : sortOption === 'selected-by-desc'
                      ? 'SEL%'
                      : 'PTS'}
                </th>
              )}
              <th className="p-2 text-right text-[10px] font-bold uppercase tracking-[0.08em]"></th>
            </tr>
          </thead>
          <tbody>
            {currentDrivers.map((driver, index) => {
              const status = selectionCheck(driver);
              const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-[#f8fbff]';

              // Find club name in the clubs array
              const clubName =
                clubs.find((c) => c.id === driver.clubId)?.name ?? 'Unknown';

              const rawValue = driver?.value;
              const numericValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue ?? 0;
              const transferTax = hasTransferTax ? numericValue * 10000 * transferTaxPercentage : 0;
              const calculatedDriverValue = hasTransferTax ? numericValue * 1000000 + transferTax : numericValue * 1000000;
              const driverSpecialty = getDriverSpecialty(driver);

              return (
                <tr
                  key={driver.id}
                  className={`${rowBg} border-t border-gray-100 cursor-pointer transition-colors hover:bg-[#edf4fb]`}
                  onClick={() => setSelectedDriver(driver)}
                >
                  <td className="pl-4 pr-2 py-1">
                    <Image
                      src={`https://ocnc-fantasy.b-cdn.net/jerseys/${driver.clubId}.png`}
                      alt={driver.name}
                      width={40}
                      height={40}
                      className="h-10 w-10 object-contain"
                      onError={(e) => {
                        e.currentTarget.src = "https://ocnc-fantasy.b-cdn.net/jerseys/906.png";
                      }}
                    />
                  </td>
                  <td className="p-1">
                    <p className="font-semibold text-gray-900">{driver.name}</p>
                    <span className="text-sm text-gray-500">{clubName}</span>
                    {driverSpecialty && (
                      <span className="block text-xs text-gray-400">{driverSpecialty}</span>
                    )}
                  </td>
                  <td className="p-1 text-right font-semibold text-gray-900">
                      {(calculatedDriverValue/1000000).toLocaleString('nl-NL', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    }).replace(/\s+/g, '')}M
                    {hasTransferTax && <div className="text-xs font-normal text-gray-500">
                      Taxe ({(transferTaxPercentage * 100).toFixed(0)}%): {(transferTax/1000000).toLocaleString('nl-NL', {
                        style: 'currency',
                        currency: 'EUR',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                      }).replace(/\s+/g, '')}M
                    </div>}
                    {status === 'overBudget' && (
                      <div className="whitespace-nowrap text-xs font-normal text-gray-500">budget dépassé</div>
                    )}
                    {status === 'clubLimit' && (
                      <div className="whitespace-nowrap text-xs font-normal text-gray-500">limite équipe</div>
                    )}
                  </td>
                  {showStatColumn && (
                    <td className="p-1 text-right">
                      {sortOption === 'points-desc' && (
                        <span className="font-semibold text-gray-900">{getDriverPoints(driver)}</span>
                      )}
                      {sortOption === 'selected-by-desc' && (
                        <span className="font-semibold text-gray-900">{getSelectedByPercentage(driver)}%</span>
                      )}
                      {sortOption === 'points-per-million-desc' && (
                        <span className="font-semibold text-gray-900">{getPointsPerMillion(driver).toFixed(1)}</span>
                      )}
                    </td>
                  )}
                  <td
                    className={`px-3 py-1 text-right align-middle ${
                      (status === 'selectable' && isEditable) || (isEditable && status === 'removable')
                        ? 'cursor-pointer'
                        : ''
                    }`}
                    onClick={
                      status === 'selectable' && isEditable
                        ? (event) => {
                            event.stopPropagation();
                            onSelect(driver);
                          }
                        : isEditable && status === 'removable'
                          ? (event) => {
                              event.stopPropagation();
                              onRemove(driver);
                            }
                          : undefined
                    }
                    onKeyDown={
                      (status === 'selectable' && isEditable) || (isEditable && status === 'removable')
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.stopPropagation();
                              if (status === 'selectable' && isEditable) {
                                onSelect(driver);
                              } else if (isEditable && status === 'removable') {
                                onRemove(driver);
                              }
                            }
                          }
                        : undefined
                    }
                    role={
                      (status === 'selectable' && isEditable) || (isEditable && status === 'removable')
                        ? 'button'
                        : undefined
                    }
                    tabIndex={
                      (status === 'selectable' && isEditable) || (isEditable && status === 'removable')
                        ? 0
                        : undefined
                    }
                  >
                    {status === 'selectable' && isEditable ? (
                      <span className="inline-flex rounded-full bg-[#FDC300] p-1 text-black">
                        <Plus size={16} />
                      </span>
                    ) : null}
                    {isEditable && status === 'removable' ? (
                      <span className="inline-flex rounded-full bg-red-500 p-1 text-white">
                        <X size={16} />
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-4 bg-gray-50 px-4 py-3 rounded-b-lg">
        <Button
          variant='pagination'
          size='icon'
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-gray-300 bg-white p-0 text-gray-700 transition-colors hover:text-[#003d60] disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronFirst />
        </Button>
        <Button
          variant='pagination'
          size='icon'
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-gray-300 bg-white p-0 text-gray-700 transition-colors hover:text-[#003d60] disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronLeft />
        </Button>
        <Select value={currentPage.toString()} onValueChange={(value) => handlePageChange(Number(value))}>
          <SelectTrigger className="w-[72px] border-gray-300 bg-white text-gray-700">
            <SelectValue placeholder="Pagina" />
          </SelectTrigger>
          <SelectContent>
            {[...Array(totalPages)].map((_, i) => (
              <SelectItem key={i + 1} value={(i + 1).toString()}>
                {i + 1}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-500">de {totalPages || 1}</span>
        <Button
          variant='pagination'
          size='icon'
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-gray-300 bg-white p-0 text-gray-700 transition-colors hover:text-[#003d60] disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronRight />
        </Button>
        <Button
          variant='pagination'
          size='icon'
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-gray-300 bg-white p-0 text-gray-700 transition-colors hover:text-[#003d60] disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronLast />
        </Button>
      </div>

      <PlayerActionModal
        isOpen={Boolean(selectedDriver)}
        onClose={() => setSelectedDriver(null)}
        driver={selectedDriver}
        clubName={selectedDriverClubName}
        onAddToTeam={
          selectedDriver && isEditable && selectedDriverStatus === 'selectable'
            ? () => onSelect(selectedDriver)
            : undefined
        }
        onRemove={
          selectedDriver && isEditable && selectedDriverStatus === 'removable'
            ? () => onRemove(selectedDriver)
            : undefined
        }
      />
    </div>
  );
};
